import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ConsolidatedCompanyKpi {
  company_id: string;
  name: string;
  faturamento: number;
  cmv: number;
}

export interface ConsolidatedKpisPayload {
  ok: boolean;
  faturamento_total: number;
  cmv_total: number;
  margem_bruta_pct: number;
  pending_rateios: number;
  companies: ConsolidatedCompanyKpi[];
  error?: string;
}

export function useConsolidatedKpis(startDate: string, endDate: string, options: { enabled?: boolean } = {}) {
  const enabled = options.enabled !== false && !!startDate && !!endDate;

  return useQuery({
    queryKey: ['consolidated-kpis', startDate, endDate],
    queryFn: async (): Promise<ConsolidatedKpisPayload | null> => {
      const { data, error } = await (supabase as any).rpc('get_consolidated_kpis', {
        p_start_date: startDate,
        p_end_date: endDate,
      });
      if (error) throw error;
      let j = data as Record<string, unknown> | null;
      if (typeof data === 'string') {
        try {
          j = JSON.parse(data) as Record<string, unknown>;
        } catch {
          j = null;
        }
      }
      if (!j || j.ok === false) {
        return {
          ok: false,
          faturamento_total: 0,
          cmv_total: 0,
          margem_bruta_pct: 0,
          pending_rateios: 0,
          companies: [],
          error: typeof j?.error === 'string' ? j.error : 'unknown',
        };
      }
      const companiesRaw = Array.isArray(j.companies) ? j.companies : [];
      const companies: ConsolidatedCompanyKpi[] = companiesRaw.map((c: Record<string, unknown>) => ({
        company_id: String(c.company_id),
        name: String(c.name ?? ''),
        faturamento: Number(c.faturamento ?? 0),
        cmv: Number(c.cmv ?? 0),
      }));
      return {
        ok: true,
        faturamento_total: Number(j.faturamento_total ?? 0),
        cmv_total: Number(j.cmv_total ?? 0),
        margem_bruta_pct: Number(j.margem_bruta_pct ?? 0),
        pending_rateios: Number(j.pending_rateios ?? 0),
        companies,
      };
    },
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
